import type { OrbExtension } from "lib/orb-qr";
import {
  extractOrbExtensionFromInstructions,
  getAdditionalDataRequiredFields,
  getPrismicTaskInstructions,
  getPrismicWorkTask,
} from "lib/prismic";
import { getLabelRelations } from "lib/label-relations";
import { getCaptureFileRequirement } from "lib/upload-files";
import {
  getOrbTaskSteps,
  getStepLabel,
  getTaskSteps,
  getStepSchemaFingerprint,
  TaskStep,
} from "lib/task-steps";
import {
  CaptureType,
  isCaptureType,
  isQrCaptureType,
  QrCaptureType,
  PrismicCollectionDetailField,
  PrismicCollectionDetails,
  PrismicTaskInstructions,
  PrismicWorkTask,
} from "types";

const CONTENT_LOAD_TIMEOUT_MS = 15_000;

async function withContentTimeout<T>(
  request: Promise<T>,
  description: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`${description} timed out`)),
      CONTENT_LOAD_TIMEOUT_MS
    );
  });

  try {
    return await Promise.race([request, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export interface ResolvedWorkTaskStep extends TaskStep {
  rtmsSequence?: ResolvedRtmsSequence;
  rtmsContextEntry?: "inherit_shared" | "ask_before_task";
  rtmsPlanFingerprint?: string;
  captureTaskIndex: number;
  captureEntryId?: string;
  hasCaptureTaskSteps: boolean;
  captureTaskId: string;
  captureTaskName: string;
  captureTaskStepNumber: number;
  isFirstCaptureTaskStep: boolean;
  captureTaskStepLabel: string;
  captureTaskStepTitle: string;
  captureType: CaptureType;
  orbExtension: OrbExtension | null;
  instructionSlices: any[];
  collectionDetailFields: PrismicCollectionDetailField[];
  captureCollectionDetailFields: PrismicCollectionDetailField[];
  stepDetailFields: PrismicCollectionDetailField[];
  labelRelations: ReturnType<typeof getLabelRelations>;
  requiredAdditionalDataFields: string[];
  maximumNumberOfCaptures?: number;
}

export interface ResolvedRtmsSequence {
  id: string;
  name: string;
  fields: PrismicCollectionDetailField[];
  firstStepNumber: number;
}

export interface ResolvedWorkTask {
  showTaskIntro: boolean;
  documentId: string;
  workTaskId: string;
  workTaskName: string;
  instructions: PrismicWorkTask["data"]["instructions"];
  sharedCollectionDetailFields: PrismicCollectionDetailField[];
  labelRelations: ReturnType<typeof getLabelRelations>;
  requiredAdditionalDataFields: string[];
  hasUploadSteps: boolean;
  steps: ResolvedWorkTaskStep[];
}

export function getOrbStepDetailFields(
  step: ResolvedWorkTaskStep
): PrismicCollectionDetailField[] {
  const seen = new Set<string>();
  return [...step.collectionDetailFields, ...step.stepDetailFields].filter(
    (field) => {
      const id = field.label_id?.toString().trim() || "";
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    }
  );
}

function getCaptureCollectionDetailFields(
  instructions: PrismicTaskInstructions["data"]
): PrismicCollectionDetailField[] {
  return (instructions.instruction_content || [])
    .filter((slice: any) => slice.slice_type === "collection_details")
    .flatMap((slice: any) => slice.items || [])
    .filter((field: PrismicCollectionDetailField) => !field.use_as_step_labels);
}

function getStepDetailFields(
  instructions: PrismicTaskInstructions["data"],
  stepNumber: number
): PrismicCollectionDetailField[] {
  return (instructions.instruction_content || [])
    .filter(
      (slice: any) =>
        slice.slice_type === "step_details" &&
        Number(slice.primary?.step_number) === stepNumber
    )
    .flatMap((slice: any) => slice.items || []);
}

function getInstructionSlices(
  instructions: PrismicTaskInstructions["data"]
): any[] {
  return (instructions.instruction_content || []).filter(
    (slice: any) => slice.slice_type === "instruction_text___image"
  );
}

export function buildResolvedWorkTask(
  documentId: string,
  expectedWorkTaskId: string,
  workTask: PrismicWorkTask["data"],
  captureTasks: PrismicTaskInstructions["data"][],
  defaultOrbExtension?: OrbExtension
): ResolvedWorkTask {
  const workTaskId = workTask.work_task_id?.toString().trim();
  if (!workTaskId || workTaskId !== expectedWorkTaskId) {
    throw new Error(`Work Task ID does not match ${expectedWorkTaskId}`);
  }

  const configuredSteps = (workTask.capture_steps || []).filter((step) =>
    Boolean(step.capture_task_id?.toString().trim())
  );
  if (configuredSteps.length === 0) {
    throw new Error(`Work Task ${workTaskId} has no capture steps`);
  }
  if (configuredSteps.length !== captureTasks.length) {
    throw new Error(`Work Task ${workTaskId} could not resolve every step`);
  }

  const sharedCollectionDetailFields = (
    workTask.collection_details || []
  ).filter((field) => Boolean(field.label_id?.toString().trim()));
  const sharedFieldIds = new Set(
    sharedCollectionDetailFields.map((field) =>
      field.label_id!.toString().trim()
    )
  );

  const executionMode = workTask.rtms_execution_mode || "individual_qr";
  if (!["individual_qr", "sequence"].includes(executionMode)) {
    throw new Error("Invalid RTMS execution mode");
  }
  const sequences = new Map<string, ResolvedRtmsSequence>();
  if (executionMode === "sequence") {
    for (const slice of (workTask.rtms_sequences || []) as any[]) {
      if (slice.slice_type !== "rtms_sequence") continue;
      const id = String(slice.primary?.sequence_id || "").trim();
      if (!id || sequences.has(id)) {
        throw new Error("RTMS sequence IDs must be nonempty and unique");
      }
      const fields = (slice.items || []) as PrismicCollectionDetailField[];
      const ids = new Set<string>();
      for (const field of fields) {
        const labelId = String(field.label_id || "").trim();
        if (!labelId || ids.has(labelId) || sharedFieldIds.has(labelId)) {
          throw new Error(`Sequence ${id} has a duplicate or invalid label ID`);
        }
        ids.add(labelId);
      }
      sequences.set(id, {
        id,
        name: String(slice.primary?.sequence_name || id),
        fields,
        firstStepNumber: 0,
      });
    }
    const closed = new Set<string>();
    let previous = "";
    for (const entry of configuredSteps) {
      const id = String(entry.rtms_sequence_id || "").trim();
      if (id !== previous && previous) closed.add(previous);
      if (
        id &&
        (entry.capture_type !== "rtms" || !sequences.has(id) || closed.has(id))
      ) {
        throw new Error(
          `RTMS sequence ${id} must reference one contiguous RTMS group`
        );
      }
      if (
        id &&
        entry.context_entry &&
        !["inherit_shared", "ask_before_task"].includes(entry.context_entry)
      ) {
        throw new Error(`Invalid participant context setting for ${id}`);
      }
      previous = id;
    }
    if (!configuredSteps.some((entry) => entry.rtms_sequence_id?.trim())) {
      throw new Error("Sequence mode requires at least one named RTMS group");
    }
  }

  let nextWorkStepNumber = 1;
  const steps = configuredSteps.flatMap<ResolvedWorkTaskStep>(
    (configuredStep, index): ResolvedWorkTaskStep[] => {
      const captureTaskId = configuredStep.capture_task_id!.toString().trim();
      const captureTask = captureTasks[index];
      if (captureTask.task_id?.toString().trim() !== captureTaskId) {
        throw new Error(
          `Capture Task ID does not match step ${index + 1} of ${workTaskId}`
        );
      }

      const configuredCaptureType = configuredStep.capture_type
        ?.toString()
        .trim();
      if (!isCaptureType(configuredCaptureType)) {
        throw new Error(
          `Capture Task ${captureTaskId} has no valid capture type in Work Task ${workTaskId}`
        );
      }
      const captureType = configuredCaptureType;
      const rtmsSequence =
        executionMode === "sequence"
          ? sequences.get(String(configuredStep.rtms_sequence_id || "").trim())
          : undefined;
      const captureTaskName =
        captureTask.task_name?.toString().trim() || captureTaskId;
      const captureCollectionDetailFields =
        getCaptureCollectionDetailFields(captureTask);
      const collectionDetailFields = captureCollectionDetailFields.filter(
        (field) => !sharedFieldIds.has(field.label_id?.toString().trim() || "")
      );
      const labelRelations = getLabelRelations(
        captureTask.instruction_content as any[]
      );
      const requiredAdditionalDataFields =
        getAdditionalDataRequiredFields(captureTask);
      const generalInstructionSlices = getInstructionSlices(captureTask);

      const orbExtension =
        captureType === "orb"
          ? extractOrbExtensionFromInstructions(captureTask) ??
            defaultOrbExtension ??
            null
          : captureType === "rtms"
          ? 0
          : null;
      if (captureType === "orb" && orbExtension === null) {
        throw new Error(
          `Capture Task ${captureTaskId} has no Orb signup extension`
        );
      }
      const captureTaskSteps = isQrCaptureType(captureType)
        ? getOrbTaskSteps(captureTask, captureType)
        : getTaskSteps(captureTask);
      const fileRequirement = getCaptureFileRequirement(captureType);
      const sourceSteps =
        captureTaskSteps.length > 0
          ? captureTaskSteps
          : [
              {
                slice: null,
                stepNumber: 1,
                stepLabel: "Step 1",
                stepTitle: captureTaskName,
                numberOfCaptures: fileRequirement.required,
              },
            ];

      return sourceSteps.map((sourceStep, sourceIndex) => {
        const stepNumber = nextWorkStepNumber++;
        if (rtmsSequence && !rtmsSequence.firstStepNumber) {
          rtmsSequence.firstStepNumber = stepNumber;
        }
        if (rtmsSequence) {
          const scopedIds = new Set(
            rtmsSequence.fields.map((field) => String(field.label_id).trim())
          );
          if (
            [
              ...captureCollectionDetailFields,
              ...getStepDetailFields(captureTask, sourceStep.stepNumber),
            ].some((field) => scopedIds.has(String(field.label_id).trim()))
          ) {
            throw new Error(
              `Task ${captureTaskId} duplicates a sequence label ID`
            );
          }
        }
        return {
          ...(rtmsSequence
            ? {
                rtmsSequence,
                rtmsContextEntry:
                  sourceIndex === 0 &&
                  configuredStep.context_entry === "ask_before_task"
                    ? ("ask_before_task" as const)
                    : ("inherit_shared" as const),
              }
            : {}),
          ...sourceStep,
          stepNumber,
          stepLabel: getStepLabel(sourceStep, stepNumber),
          stepTitle: sourceStep.stepTitle || captureTaskName,
          captureTaskIndex: index,
          captureEntryId: configuredStep.capture_entry_id?.trim() || undefined,
          hasCaptureTaskSteps: captureTaskSteps.length > 0,
          captureTaskId,
          captureTaskName,
          captureTaskStepNumber: sourceStep.stepNumber,
          isFirstCaptureTaskStep: sourceIndex === 0,
          captureTaskStepLabel: getStepLabel(sourceStep, stepNumber),
          captureTaskStepTitle: sourceStep.stepTitle || captureTaskName,
          captureType,
          orbExtension,
          instructionSlices: [
            ...(isQrCaptureType(captureType) || sourceIndex === 0
              ? generalInstructionSlices
              : []),
            ...(sourceStep.slice ? [sourceStep.slice] : []),
          ],
          collectionDetailFields,
          captureCollectionDetailFields,
          stepDetailFields: getStepDetailFields(
            captureTask,
            sourceStep.stepNumber
          ),
          labelRelations,
          requiredAdditionalDataFields,
          maximumNumberOfCaptures:
            captureTaskSteps.length > 0
              ? sourceStep.numberOfCaptures
              : fileRequirement.maximum,
        };
      });
    }
  );

  for (const sequence of Array.from(sequences.values())) {
    const members = steps.filter(
      (step) => step.rtmsSequence?.id === sequence.id
    );
    const plan = JSON.stringify({
      version: 1,
      sequenceId: sequence.id,
      fields: sequence.fields,
      steps: members.map((step) => ({
        number: step.stepNumber,
        taskId: step.captureTaskId,
        taskStep: step.captureTaskStepNumber,
        context: step.rtmsContextEntry,
        schema: getStepSchemaFingerprint({
          captureType: "rtms",
          numberOfCaptures: step.numberOfCaptures,
          fields: getOrbStepDetailFields(step),
          stepLabel: step.stepLabel,
        }),
      })),
    });
    members.forEach((step) => {
      step.rtmsPlanFingerprint = plan;
    });
  }

  return {
    documentId,
    workTaskId,
    workTaskName:
      workTask.work_task_name?.toString().trim() || expectedWorkTaskId,
    instructions: workTask.instructions,
    showTaskIntro: workTask.show_task_intro !== false,
    sharedCollectionDetailFields,
    labelRelations: getLabelRelations(workTask.label_relations as any[]),
    requiredAdditionalDataFields: Array.from(
      new Set(captureTasks.flatMap(getAdditionalDataRequiredFields))
    ),
    hasUploadSteps: steps.some((step) => !isQrCaptureType(step.captureType)),
    steps,
  };
}

async function loadResolvedWorkTask(
  documentId: string,
  workTaskId: string,
  selectedCollection?: PrismicCollectionDetails
): Promise<ResolvedWorkTask> {
  const workTask = await withContentTimeout(
    getPrismicWorkTask(documentId),
    `Loading Work Task ${workTaskId}`
  );
  if (!workTask) {
    throw new Error(`Work Task ${workTaskId} is unavailable`);
  }

  const captureTaskIds = (workTask.capture_steps || [])
    .map((step) => step.capture_task_id?.toString().trim())
    .filter((taskId): taskId is string => Boolean(taskId));
  const captureTasks = await Promise.all(
    captureTaskIds.map(async (captureTaskId) => {
      const captureTask = await withContentTimeout(
        getPrismicTaskInstructions(captureTaskId, selectedCollection),
        `Loading Capture Task ${captureTaskId}`
      );
      if (!captureTask) {
        throw new Error(`Capture Task ${captureTaskId} is unavailable`);
      }
      return captureTask;
    })
  );

  return buildResolvedWorkTask(documentId, workTaskId, workTask, captureTasks);
}

export function getResolvedWorkTask(
  documentId: string,
  workTaskId: string,
  selectedCollection?: PrismicCollectionDetails
): Promise<ResolvedWorkTask> {
  return loadResolvedWorkTask(documentId, workTaskId, selectedCollection);
}

export function buildResolvedOrbTask(
  instructions: PrismicTaskInstructions["data"]
): ResolvedWorkTask {
  return buildResolvedQrTask(instructions, "orb");
}

export function buildResolvedQrTask(
  instructions: PrismicTaskInstructions["data"],
  captureType: QrCaptureType
): ResolvedWorkTask {
  const taskId = instructions.task_id?.toString().trim();
  if (!taskId)
    throw new Error(
      `${captureType === "rtms" ? "RTMS" : "Orb"} capture task has no Task ID`
    );
  const task = buildResolvedWorkTask(
    "",
    taskId,
    {
      work_task_id: taskId,
      work_task_name: instructions.task_name,
      show_task_intro: instructions.show_task_intro,
      instructions: [],
      collection_details: [],
      label_relations: [],
      capture_steps: [{ capture_task_id: taskId, capture_type: captureType }],
    },
    [instructions],
    0
  );
  return {
    ...task,
    sharedCollectionDetailFields:
      getCaptureCollectionDetailFields(instructions),
    labelRelations: getLabelRelations(
      instructions.instruction_content as any[]
    ),
    steps: task.steps.map((step) => ({
      ...step,
      collectionDetailFields: [],
      labelRelations: [],
    })),
  };
}
